// web/src/screens/picker.ts — UI-02
//
// Renders the game picker as a vertical stack of horizontal "Spēle nr. N."
// pill bars per docs/sketch/Logopediskas-speles.pdf (slide 1). The trailing
// "un vēl vismaz 20 spēles...." bar is purely visual — not part of TILES,
// not clickable, doesn't affect final-screen "Nākamā spēle" logic.
//
// CR-02 (Phase-04 review): the FE reads game UUIDs from window.__GAMES__,
// a short_code → UUID map injected by the Go HTML shell at boot
// (see internal/httpx/web_embed.go::renderShellAtBoot). This avoids a
// pre-launch placeholder substitution AND a Phase-5 /api/games roundtrip.
// On a misconfigured server (empty __GAMES__) the wired tile falls
// through to the "Drīzumā!" toast — same UX as an unwired tile, no crash.

import { startSession } from "../api/sessions.js";
import { navigate } from "../main.js";
import type { GameListItem } from "../types/api.js";

declare global {
  interface Window {
    // short_code → UUID, injected by the Go HTML shell at boot (D-05).
    __GAMES__?: Record<string, string>;
    // v3.2 (migration 0038): short_code → title map, populated by
    // ensureGameMap() from /api/games. Used by the picker to render the
    // tile labels from server data and by intro.ts via sessionStorage.
    __GAMES_TITLES__?: Record<string, string>;
    // Optional pre-rendered "soft-fail" praise audio URL injected by the Go
    // shell.
    __SOFTFAIL_PRAISE_URL__?: string;
  }
}

export interface Tile {
  shortCode: string;
  title: string;
  themeClass: string;
  themeColor: string;
  isWired: boolean;
}

/**
 * D-05: window.__GAMES__ is the hot path (injected by the Go template at
 * boot). If empty/missing (e.g. dev mode without MountWeb wiring, or a Go
 * server that hasn't shipped the games slice yet), fall back to
 * GET /api/games so the picker still functions.
 *
 * On any failure (network, non-2xx, JSON parse) we leave __GAMES__
 * undefined and let tile clicks fall through to the "Drīzumā!" toast —
 * same UX as an unwired tile, never a crash, never a bogus session start.
 */
async function ensureGameMap(): Promise<void> {
  if (window.__GAMES__ && Object.keys(window.__GAMES__).length > 0
      && window.__GAMES_TITLES__ && Object.keys(window.__GAMES_TITLES__).length > 0) {
    return;
  }
  try {
    const res = await fetch("/api/games", { credentials: "include" });
    if (!res.ok) return;
    const items = (await res.json()) as GameListItem[];
    window.__GAMES__ = Object.fromEntries(items.map((i) => [i.short_code, i.id]));
    window.__GAMES_TITLES__ = Object.fromEntries(items.map((i) => [i.short_code, i.title]));
  } catch {
    /* keep undefined; tiles fall through to "Drīzumā!" */
  }
}

// shortCode is the lookup key into window.__GAMES__ (which the Go HTML
// shell populates from the boot-time games slice). Order is the on-screen
// order; final.ts uses this same ordering for "Nākamā spēle".
// themeColor mirrors the per-class --theme-color CSS variable in style.css.
// Duplicated here so card.ts can read the active theme color from
// sessionStorage without round-tripping through getComputedStyle.
export const TILES: Tile[] = [
  { shortCode: "vocab",          title: "Spēle nr. 1.  Nepietiekams vārdu krājums latviešu valodā",                     themeClass: "theme-vocab",          themeColor: "#5E3FBE", isWired: true },
  { shortCode: "first_sound",    title: "Spēle nr. 2. Vārda pirmās skaņas atpazīšana",                                  themeClass: "theme-first_sound",    themeColor: "#B43FA8", isWired: true },
  { shortCode: "reading",        title: "Spēle nr. 3. Lasītprasmes pamati (no viegliem vārdiem līdz 3-zilbju vārdiem)", themeClass: "theme-reading",        themeColor: "#3F8E96", isWired: true },
  { shortCode: "letter_sound",   title: "Spēle nr. 4. Trenē atsevišķu burtu izrunu!",                                   themeClass: "theme-letter_sound",   themeColor: "#0F5C7A", isWired: true },
  { shortCode: "missing_letter", title: "Spēle nr. 5. Trūkstošais burts vārdā",                                         themeClass: "theme-missing_letter", themeColor: "#D72E2E", isWired: true },
];

export function gameIdFor(shortCode: string): string | undefined {
  return window.__GAMES__?.[shortCode];
}

const BAR_BASE_STYLE = `
  border: 0; border-radius: 6px;
  padding: 14px 22px;
  min-height: 64px;
  width: max-content; max-width: 100%;
  display: inline-flex; align-items: center;
  text-align: left;
  color: var(--text-on-color);
  font-family: var(--font-fancy);
  font-size: 36px; font-weight: 400;
  line-height: 1.25;
`;

// Light cyan fill for the non-clickable "more games coming" bar — distinct
// from the 5 wired-game theme colors so it reads as a promise, not a target.
const PROMISE_BAR_COLOR = "#1FB6CC";

export function mount(root: HTMLElement, _props?: unknown): () => void {
  document.body.className = ""; // strip prior theme

  // D-05: hydrate the boot-injected game map from /api/games when absent.
  // Fire-and-forget — by the time the user taps a tile (clicks must wait
  // for at least one render frame), the map is populated. If it isn't,
  // gameIdFor returns undefined and the click falls through to "Drīzumā!".
  void ensureGameMap();

  const bars = TILES.map((t, i) => `
    <button
      class="game-bar ${t.themeClass}"
      data-idx="${i}"
      data-short-code="${t.shortCode}"
      style="${BAR_BASE_STYLE} background: var(--theme-color); cursor: pointer;"
    >${t.title}</button>
  `).join("");

  const promiseBar = `
    <div
      class="game-bar promise-bar"
      style="${BAR_BASE_STYLE} background: ${PROMISE_BAR_COLOR}; cursor: default;"
      aria-disabled="true"
    >un vēl vismaz 20 spēles....</div>
  `;

  root.innerHTML = `
    <section class="screen-picker" style="
      --picker-bg: url('/storage/images/_static/blue-sky-mountains.png');
      min-height: 100dvh;
      padding: var(--space-2xl) var(--space-lg);
      display: flex;
      align-items: center; justify-content: center;
    ">
      <button
        class="config-btn"
        aria-label="Iestatījumi"
        style="position: fixed; top: var(--space-md); right: var(--space-md); z-index: 10; background: none; border: 0; font-size: 28px; line-height: 1; padding: var(--space-sm); min-width: 44px; min-height: 44px; cursor: pointer; opacity: 0.7;"
      >⚙️</button>
      <div class="game-list-panel">
        <div class="tile-stack" style="
          display: flex; flex-direction: column;
          align-items: flex-start; gap: var(--space-md);
          max-width: 100%;
        ">
          ${bars}
          ${promiseBar}
        </div>
      </div>
      <p
        class="toast"
        role="status"
        style="position: fixed; bottom: var(--space-lg); left: 50%; transform: translateX(-50%); background: var(--text-primary); color: var(--text-on-color); padding: var(--space-md) var(--space-lg); border-radius: 8px; opacity: 0; transition: opacity 200ms; pointer-events: none;"
      ></p>
    </section>
  `;

  const tiles = root.querySelectorAll<HTMLButtonElement>(".game-bar:not(.promise-bar)");
  const toast = root.querySelector(".toast") as HTMLElement;

  // ⚙️ → /config (player profile, TTS voice, turn timeouts, mic beep,
  // privacy). The picker is a chrome-free 1:1 PDF replica, so the settings
  // entry point floats in the top-right corner rather than living in a header.
  const cfgBtn = root.querySelector(".config-btn") as HTMLButtonElement;
  function onCfg() { navigate("/config"); }
  cfgBtn.addEventListener("click", onCfg);

  function showToast(msg: string, durationMs = 1200) {
    toast.textContent = msg;
    toast.style.opacity = "1";
    setTimeout(() => { toast.style.opacity = "0"; }, durationMs);
  }

  async function onTileClick(e: Event) {
    const btn = e.currentTarget as HTMLButtonElement;
    const idx = Number(btn.dataset["idx"]);
    const tile = TILES[idx];
    if (tile === undefined || !tile.isWired) {
      showToast("Drīzumā!");
      return;
    }
    const gameId = gameIdFor(tile.shortCode);
    if (!gameId) {
      // CR-02: no UUID injected by the Go shell (or shortCode not found).
      // Same toast as an unwired tile — never crash, never send a bogus id.
      showToast("Drīzumā!");
      console.warn("picker: no game UUID for short_code", tile.shortCode);
      return;
    }
    document.body.className = tile.themeClass;
    try {
      const resp = await startSession({ game_id: gameId });
      // Cache session data for the card screen and intro screen.
      // v2 shape (10-09): server returns only {session_id}; the v1 priming
      // fields are gone — card.ts pulls the first task via the /turn init
      // call. themeColor mirrors the picked tile so card.ts can re-apply
      // the theme color to dynamic elements without re-reading the CSS class.
      // v3.2 (migration 0038): the game's authored title flows through
      // sessionStorage so the intro screen can display it (instead of
      // the previously hard-coded per-game TITLE_FOR map in intro.ts).
      const titleForSession = window.__GAMES_TITLES__?.[tile.shortCode] ?? tile.title;
      sessionStorage.setItem("currentSession", JSON.stringify({
        sessionId: resp.session_id,
        gameShortCode: tile.shortCode,
        gameTitle: titleForSession,
        themeColor: tile.themeColor,
        totalCards: resp.total_cards,
      }));
      navigate("/preflight");
    } catch (err) {
      showToast("Zaudēts savienojums ar serveri", 2400);
      console.error("startSession failed", err);
    }
  }

  tiles.forEach(t => t.addEventListener("click", onTileClick));

  return () => {
    tiles.forEach(t => t.removeEventListener("click", onTileClick));
    cfgBtn.removeEventListener("click", onCfg);
  };
}
